import { Button, ButtonMode, ButtonSize, ICONS } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { ReactElement, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';
import { toasterState } from '../../../atoms/toaster.atom';
import Validator from '../../../components/validator/Validator';
import useSaveProfile from '../../../hooks/user/useSaveProfile';

function ChangePassword(): ReactElement {
  const showToaster = useSetRecoilState(toasterState);
  const {
    register,
    watch,
    formState: { errors },
    handleSubmit,
    reset: resetForm,
  } = useForm();
  const [newPassword, confirmPassword] = watch(['newPassword', 'confirmPassword']);
  const [message, setMessage] = useState('');
  const { mutate: saveProfile, isLoading: isSaveLoading } = useSaveProfile();

  const validators = [
    {
      regex: /(.+){8,}/,
      message: '8 or more characters',
    },
    {
      regex: /[A-Z]+/,
      message: '1 upper case character',
    },
    {
      regex: /[a-z]+/,
      message: '1 lower case character',
    },
    {
      regex: /[0-9]+/,
      message: '1 number',
    },
    {
      regex: /[$*@!#%&()^~{},.+-]+/,
      message: '1 special character',
    },
  ];

  useEffect(() => {
    if (newPassword !== confirmPassword) {
      setMessage("Passwords don't match");
    } else {
      setMessage('');
    }
  }, [newPassword, confirmPassword]);

  const validate = () => {
    for (const validator of validators) {
      if (!validator.regex.test(newPassword)) {
        return false;
      }
    }

    return true;
  };

  const onSubmit = () => {
    if (newPassword && !message && validate()) {
      saveProfile(
        { password: newPassword },
        {
          onSuccess: () => {
            showToaster({ text: 'Your password was updated' });
            resetForm();
          },
        }
      );
    }
  };

  return (
    <div className="cs-change-password-ctx">
      <div className="profile-section">
        <div className="title">Change Password</div>
        <form className="ui form" onSubmit={handleSubmit(onSubmit)}>
          <div className="field">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label>New Password</label>
            <input type="password" className={classNames({ error: errors.newPassword })} {...register('newPassword', { required: true })} placeholder="Enter New Password" />
            <div className="validators">
              {validators.map((validator, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Validator key={`validator-${index}`} regex={validator.regex} value={newPassword} icon={ICONS.Check} message={validator.message} />
              ))}
            </div>
          </div>
          <div className="field">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label>Confirm Password</label>
            <input type="password" className={classNames({ error: errors.confirmPassword })} {...register('confirmPassword', { required: true })} placeholder="Confirm New Password" />
            {errors.confirmPassword && <div className="error-message">Password confirmation is required.</div>}
          </div>
          {message && <div className="error-message">{message}</div>}
          <div className="buttons-ctx">
            <Button type="submit" mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isSaveLoading} disabled={isSaveLoading}>
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChangePassword;
